/**
 * NodeView.tsx — Página dedicada a uma nota (/node/:id)
 *
 * Layout responsivo:
 *  - Desktop (≥769px): nota à esquerda, chat à direita (sidebar 360px)
 *  - Mobile (<769px): nota fullwidth, chat vira bottom sheet colapsável
 */

import { useParams } from 'react-router-dom'
import { NodePanel } from '../components/NodePanel'
import { ChatNode } from '../components/ChatNode'
import { useState, useEffect } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  )
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [breakpoint])
  return isMobile
}

export function NodeView() {
  const { id } = useParams<{ id: string }>()
  const [noteTitle, setNoteTitle] = useState('...')
  const isMobile = useIsMobile()
  // Desktop: chat sempre aberto; mobile: começa minimizado
  const [chatOpen, setChatOpen] = useState(false)

  useEffect(() => {
    if (!id) return
    getDoc(doc(db, 'vault_notes', id)).then((snap) => {
      if (snap.exists()) setNoteTitle(snap.data().title as string)
    })
  }, [id])

  if (!id) return <div className="loading-screen">ID inválido.</div>

  // ============ MOBILE: stack vertical + chat bottom sheet ============
  if (isMobile) {
    return (
      <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
        {/* Nota ocupa tela toda */}
        <div
          style={{
            height: '100%',
            overflowY: 'auto',
            paddingBottom: chatOpen ? '70vh' : '64px',
            transition: 'padding-bottom 200ms ease',
          }}
        >
          <NodePanel noteId={id} />
        </div>

        {/* Chat bottom sheet */}
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            height: chatOpen ? '70vh' : '52px',
            background: 'rgba(13, 13, 30, 0.97)',
            borderTop: '1px solid rgba(87, 245, 195, 0.25)',
            backdropFilter: 'blur(20px)',
            transition: 'height 250ms ease',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 100,
            boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
          }}
        >
          {/* Handle / header sempre visível */}
          <button
            onClick={() => setChatOpen((v) => !v)}
            style={{
              height: '52px',
              flexShrink: 0,
              background: 'none',
              border: 'none',
              color: '#57f5c3',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 18px',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            <span>
              {chatOpen ? '▾' : '▸'} Chat sobre esta nota
            </span>
            <span style={{ fontSize: '11px', color: '#9090b0', fontWeight: 400 }}>
              Gemini · RAG
            </span>
          </button>

          {/* Conteúdo do chat */}
          {chatOpen && (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                padding: '0 12px 12px',
              }}
            >
              <ChatNode noteId={id} noteTitle={noteTitle} />
            </div>
          )}
        </div>
      </div>
    )
  }

  // ============ DESKTOP: lado a lado ============
  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <NodePanel noteId={id} />
      </div>

      <div
        style={{
          width: chatOpen ? '360px' : '44px',
          borderLeft: '1px solid rgba(255,255,255,0.06)',
          padding: chatOpen ? '20px 16px' : '12px 6px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          overflowY: 'auto',
          transition: 'width 200ms ease, padding 200ms ease',
          background: 'rgba(13, 13, 30, 0.5)',
        }}
      >
        <button
          onClick={() => setChatOpen((v) => !v)}
          style={{
            background: 'none',
            border: 'none',
            color: '#57f5c3',
            cursor: 'pointer',
            fontSize: '14px',
            padding: '4px',
            alignSelf: chatOpen ? 'flex-end' : 'center',
            opacity: 0.8,
          }}
          title={chatOpen ? 'Minimizar chat' : 'Abrir chat'}
        >
          {chatOpen ? '→' : '✦'}
        </button>

        {chatOpen && (
          <>
            <h3
              style={{
                fontSize: '13px',
                color: '#9090b0',
                fontWeight: 400,
                margin: 0,
              }}
            >
              Chat sobre esta nota
            </h3>
            <ChatNode noteId={id} noteTitle={noteTitle} />
          </>
        )}
      </div>
    </div>
  )
}
